import { Card, Inline, Stack, StatePill, type StatePillTone } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../lib/api.js";
import { useSession } from "../session/SessionProvider.js";
import { usePlatformOwner } from "../access/platform-owner.js";

/**
 * GT-GFI-ADMIN-COPILOT-RISK-RADAR-SHELL-01 (Sprint E) — compact, read-only
 * "Customer Risk Radar" card for the Admin overview.
 *
 * Platform-Owner only (rendered as nothing for anyone else; the API also
 * 403s). Deterministic — no AI provider is active. Surfaces the
 * cross-tenant AI-health risk + an HONEST instrumentation summary
 * (missing signals shown as gaps, never as healthy). No raw payloads / no
 * write actions.
 */

type RiskLevel = "ok" | "watch" | "warning" | "critical" | "unknown";

interface RiskRadar {
  summary: {
    byLevel: Record<RiskLevel, number>;
    totalTenants: number;
    instrumentedTenants: number;
    notInstrumentedTenants: number;
  };
  tenants: {
    tenantId: string;
    displayName: string | null;
    riskLevel: RiskLevel;
  }[];
  inventory: { availability: "exists" | "partial" | "not_instrumented" }[];
}

const LEVEL_TONE: Record<RiskLevel, StatePillTone> = {
  ok: "success",
  watch: "info",
  warning: "warn",
  critical: "danger",
  unknown: "neutral",
};

/**
 * @param onOpenFullView optional CTA. When provided (e.g. from the Overview
 *   page), the card renders an "Open Risk Radar & Delivery Health" button
 *   that navigates to the dedicated Admin AI Copilot page. Optional so the
 *   card stays usable standalone (and its own test needs no nav provider).
 */
export function AdminRiskRadarCard({ onOpenFullView }: { onOpenFullView?: () => void } = {}) {
  const { t } = useLocale();
  const session = useSession();
  const owner = usePlatformOwner(session);
  const query = useQuery<{ radar: RiskRadar }, Error>({
    queryKey: ["admin", "ai", "risk-radar"],
    queryFn: ({ signal }) =>
      apiGet<{ radar: RiskRadar }>("/v1/admin/ai/risk-radar", undefined, signal),
    enabled: owner,
    staleTime: 30_000,
  });

  if (!owner) return null;

  const radar = query.data?.radar ?? null;
  const byLevel = radar?.summary.byLevel;
  const needsAttention = (byLevel?.warning ?? 0) + (byLevel?.critical ?? 0);
  const unknown = byLevel?.unknown ?? 0;
  const live = radar ? radar.inventory.filter((s) => s.availability === "exists").length : 0;
  const partial = radar ? radar.inventory.filter((s) => s.availability === "partial").length : 0;
  const gap = radar
    ? radar.inventory.filter((s) => s.availability === "not_instrumented").length
    : 0;

  const flagged = (radar?.tenants ?? [])
    .filter((tr) => tr.riskLevel === "warning" || tr.riskLevel === "critical")
    .slice(0, 5);

  const levelLabel = (level: RiskLevel) => t(`admin.overview.risk_radar.level.${level}` as const);

  return (
    <Card elevation={1}>
      <Stack gap={2}>
        <Inline gap={2} justify="space-between" align="center" wrap>
          <strong>{t("admin.overview.risk_radar.section.title")}</strong>
          <span
            style={{ color: "var(--gt-sys-text-muted)", fontSize: "var(--gt-sys-font-size-xs)" }}
          >
            {t("admin.overview.risk_radar.note")}
          </span>
        </Inline>

        <Stack gap={1} data-testid="admin-risk-radar-summary">
          <KeyValue
            label={t("admin.overview.risk_radar.needs_attention")}
            value={radar ? String(needsAttention) : "…"}
          />
          <KeyValue
            label={t("admin.overview.risk_radar.unknown")}
            value={radar ? String(unknown) : "…"}
          />
          <KeyValue
            label={t("admin.overview.risk_radar.instrumented")}
            value={
              radar ? `${radar.summary.instrumentedTenants} / ${radar.summary.totalTenants}` : "…"
            }
          />
          <KeyValue
            label={t("admin.overview.risk_radar.coverage")}
            value={radar ? `${live} / ${partial} / ${gap}` : "…"}
          />
        </Stack>

        {radar ? (
          flagged.length > 0 ? (
            <Stack gap={1} data-testid="admin-risk-radar-flagged">
              {flagged.map((tr) => (
                <Inline key={tr.tenantId} gap={2} justify="space-between" align="center" wrap>
                  <span style={{ fontSize: "var(--gt-sys-font-size-sm)" }}>
                    {tr.displayName ?? tr.tenantId}
                  </span>
                  <StatePill tone={LEVEL_TONE[tr.riskLevel]} label={levelLabel(tr.riskLevel)} />
                </Inline>
              ))}
            </Stack>
          ) : (
            <span
              style={{ color: "var(--gt-sys-text-muted)", fontSize: "var(--gt-sys-font-size-sm)" }}
            >
              {t("admin.overview.risk_radar.none_flagged")}
            </span>
          )
        ) : null}

        <span style={{ color: "var(--gt-sys-text-muted)", fontSize: "var(--gt-sys-font-size-xs)" }}>
          {t("admin.overview.risk_radar.delivery_note")}
        </span>

        {onOpenFullView ? (
          <button
            type="button"
            onClick={onOpenFullView}
            data-testid="admin-risk-radar-open-full"
            style={{
              appearance: "none",
              alignSelf: "flex-start",
              border: "1px solid var(--gt-sys-border)",
              background: "var(--gt-sys-surface)",
              color: "var(--gt-sys-accent)",
              paddingBlock: "var(--gt-sys-space-1)",
              paddingInline: "var(--gt-sys-space-3)",
              borderRadius: "var(--gt-sys-radius-sm)",
              font: "inherit",
              fontWeight: "var(--gt-sys-font-weight-semibold)",
              cursor: "pointer",
            }}
          >
            {t("admin.overview.risk_radar.open_full")}
          </button>
        ) : null}
      </Stack>
    </Card>
  );
}

function KeyValue({ label, value }: { label: string; value: string }) {
  return (
    <Inline justify="space-between" align="center" wrap>
      <span style={{ color: "var(--gt-sys-text-muted)", fontSize: "var(--gt-sys-font-size-sm)" }}>
        {label}
      </span>
      <code style={{ fontSize: "var(--gt-sys-font-size-sm)" }}>{value}</code>
    </Inline>
  );
}
